/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { service } from '@ember/service';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import moment from 'moment-timezone';
import {
  DirectionsOptions,
  PeriodsOptions,
  DynamicCurrentDate,
  DynamicDatePrefix
} from 'volta/models/constants/date-filters';

import type { TDirectionOption, TPeriodOption } from 'volta/models/constants/date-filters';

import type { IntlService } from 'ember-intl';

interface DynamicDateFilterArgs {
  value: string;
  valueDidChange: (isFromCalendar: boolean, newValue?: Date | string) => void;
  handleDynamicValue: (value: string) => void;
  dynamicValue?: string;
}

export default class DynamicDateFilter extends Component<DynamicDateFilterArgs> {
  @service intl!: IntlService;
  directions = DirectionsOptions(this.intl);
  periods = PeriodsOptions(this.intl);
  dynamicCurrentDate = DynamicCurrentDate;

  /**
   * Getters
   */

  get value() {
    if (moment.utc(this.args.value).isValid()) {
      return this.args.value;
    }

    const dynamicValue = this.dynamicValue;
    switch (this.args.value) {
      case DynamicCurrentDate:
        return moment.utc();
      default:
        if (dynamicValue) {
          if (dynamicValue.direction?.key === 'last') {
            return moment.utc().subtract(
              dynamicValue.count,
              dynamicValue.period?.key as moment.unitOfTime.DurationConstructor
            );
          }
          return moment.utc().add(
            dynamicValue.count,
            dynamicValue.period?.key as moment.unitOfTime.DurationConstructor
          );
        }
    }
    return undefined;
  }

  get dateFunction() {
    const dynamicValue = this.dynamicValue;
    return dynamicValue
      ? `${DynamicDatePrefix}${dynamicValue.direction?.key}_${dynamicValue.period?.key}_${dynamicValue.count}`
      : undefined;
  }

  get dynamicValue() {
    const { value } = this.args;
    if (value?.startsWith(DynamicDatePrefix)) {
      const [_prefix, direction, period, count] = value.split('_');
      const parsedCount = Number(count);
      if (count === '' || Number.isNaN(parsedCount)) {
        return undefined;
      }
      const parsedPeriod = this.periods.find((p) => p.key === period) ?? this.periods[0];
      const parsedDirection =
        this.directions.find((d) => d.key === direction) ?? this.directions[0];

      return { direction: parsedDirection, period: parsedPeriod, count: parsedCount };
    } else {
      return undefined;
    }
  }

  get dynamicValueError() {
    return this.args.value?.startsWith(DynamicDatePrefix) &&
      this.args.value != this.dynamicCurrentDate &&
      !this.dynamicValue
      ? this.intl.t('filterDefinitions.dynamicFilterError')
      : undefined;
  }

  /**
   * Actions
   */

  @action
  selectDirection(direction: TDirectionOption) {
    this.updateDynamicValue(this.buildDynamicValue(direction, undefined, undefined));
  }

  @action
  selectPeriod(period: TPeriodOption) {
    this.updateDynamicValue(this.buildDynamicValue(undefined, period, undefined));
  }

  @action
  changeCount(event: InputEvent) {
    const value = Number((event.target as HTMLInputElement).value);

    if (!Number.isNaN(value) && value > 0) {
      this.updateDynamicValue(this.buildDynamicValue(undefined, undefined, value));
    }
  }

  @action
  updateDynamicValue(dynamicValue: string) {
    this.args.handleDynamicValue(dynamicValue);
  }

  @action
  handleCheckDynamicValue() {
    this.args.handleDynamicValue(this.buildDynamicValue());
  }

  buildDynamicValue(direction?: TDirectionOption, period?: TPeriodOption, count?: number) {
    const {
      dynamicValue = {
        direction: this.directions[0],
        period: this.periods[0],
        count: 1
      }
    } = this;
    const dyn = {
      direction: direction?.key ?? dynamicValue.direction?.key,
      period: period?.key ?? dynamicValue.period?.key,
      count: count ?? dynamicValue.count
    };
    return `${DynamicDatePrefix}${dyn.direction}_${dyn.period}_${dyn.count}`;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Filters::DynamicDateFilter': typeof DynamicDateFilter;
    'filters/dynamic-date-filter': typeof DynamicDateFilter;
  }
}
